<template>
	<div>
		<section>
			<h2 class="big-title">Usage Guides</h2>

			<h2>Install</h2>

			<ul>
				<li>Rename data.win in the DEADBOLT folder to something else (eg. data-vanilla.win).</li>
				<li>Drop all the files from the "Game files" folder into the DEADBOLT folder.</li>
			</ul>

			<h2>Enable features</h2>

			<p>All mod options are disabled by default. Enable them in <code>DEADBOLT/JSON/globalconfig.json</code></p>
			<p>Some settings will take effect when you restart the level, others need the game to be restarted (the game restart hotkey is Pause/Break, next to Scroll Lock)</p>

			<h2>Saving</h2>

			<p>Saving is now possible since 1.4.0. Saves are both made <span class="color-r">and loaded</span> from a new folder, inside Deadbolt's usual save folder:</p>
			<p><code>%LocalAppData%\deadbolt_game\<span class="color-b">Saves</span>\DCP</code></p>
			<p>To transfer existing vanilla save files, just copy them into this folder.</p>
			<p>If you are playing a custom campaign fromthe workshop, save files will be made in a workshop ID folder, ie:</p>
			<p><code>%LocalAppData%\deadbolt_game\<span class="color-b">Saves</span>\(workshop_id)</code></p>

			<h2>Custom levels</h2>

			<p>Like save files, custom levels also have their own folder in the deadbolt_game appdata folder. The full path is:</p>
			<p><code>%LocalAppData%\deadbolt_game\<span class="color-b">Levels</span></code></p>


			<h3>Using custom.json</h3>

			<p>Set new level options with <span class="color-y">custom.json</span>. A starter version is inside the DCP zip, in the Workshop folder.</p>
			<p>To use it, place it inside your <span class="color-b">Levels</span> folder. It will affect all other levels in that folder.</p>
			<p>To configure it, use this app or edit it manually. The full list of settings is below.</p>

			<h3>Publishing with custom.json</h3>

			<p>To publish levels with <span class="color-y">custom.json</span>, upload your level as a zip containing your level plus <span class="color-y">custom.json</span>. You can subscribe to <a href="https://steamcommunity.com/sharedfiles/filedetails/?id=2190078536">this workshop item</a> and open the item's files on your computer to see this in action.</p>

			<h3>Settings for custom.json</h3>

			<p>Here's what each setting in custom.json does:</p>
			<LevelConfigSettingsTable/>
		</section>

		<section>
			<h2 class="big-title">Workshop</h2>

			<h2>Playing Custom Campaigns/Dialogue</h2>

			<p><span class="color-r">Note: Custom campaigns is a WIP feature. The approach outlined here will be refined.</span></p>
			<p>Update 1.3.0 made it possible to load custom <strong>dialogue</strong> or entire <strong>campaigns</strong> from the Steam Workshop.</p>
			<p>To use a custom campaign or dialogue set, edit <code>JSON\globalconfig.json</code> and enter the item's workshop item's ID in the relevant part of the <code>path</code> settings.</p>
			<p>For example, to load the levels and dialogue from item <a href="https://steamcommunity.com/sharedfiles/filedetails/?id=2215574282">2215574282</a>, edit your path settings to be:</p>
			<pre><code>"paths": {
		"workshop_id_campaign_stages": "2215574282",
		"workshop_id_campaign_dialogue": "2215574282",
		"level_filename_prefix": "dcp_"
	},</code></pre>
			<p>The 3rd setting depends on the workshop item. Please refer to the item's description to find out if it's needed.</p>
			<p>To undo these, replace the value of all 3 settings with <code>false</code>.</p>

			<h2>Publishing Custom Campaigns/Dialogue</h2>
			<p>To publish a custom campaign or dialogue set, your levels or dialogue must be put into a folder named:</p>
			<ul>
				<li><code>Stages</code> - for custom levels (*.nc files)</li>
				<li><code>Dialogue</code> - for custom dialog (*.json files)</li>
			</ul>
			<p>Your zip should only contain one or both of these folders.</p>
			<!-- <p>Example:</p> -->
			<!-- <p><img src="Misc/Readme-Screenshots/workshop-zip-file.png"></p> -->
			<p>If your levels use a <span class="color-y">custom.json</span> file, put it inside the Stages folder.</p>
			<p>Files <strong>must</strong> be named the same as in the base game, for both dialogue and levels.</p>

			<h4>Custom level names</h4>
			<p>To bypass game logic tied to level filenames, level files <em>can</em> be prefixed.</p>
			<p>This prefix should be specified in the player's globalconfig.json, under <code>level_filename_prefix</code> (see above).</p>

			<h4>Fallbacks</h4>
			<p>Since v1.4.0, if a dialogue JSON file is missing, the vanilla dialogue JSON file will be used instead. However, there is currently no fallback for levels.</p>
		</section>

		<section>
			<h2 class="big-title">Misc</h2>

			<h2>Edit weapons</h2>

			<ul>
				<li>Edit weapons in <code>DEADBOLT/JSON/weapons.json</code></li>
				<li>Some weapon stats are still hardcoded, particularly for guns (eg. full/semi-auto)</li>
				<li>"Bullet" weapons are templates, and are not usable weapons. Their stats are used for special weapons, traps, and shotguns.</li>
			</ul>
			<h2>Edit weapons as a spreadsheet</h2>
			<ul>
				<li><code>weapons.json</code> is exported from my <a href="https://docs.google.com/spreadsheets/d/1qRyF3laHK2bKYyI552aA5PElzib8ak1FfLNxDId9Lzg/edit#gid=482879943">master DB spreadsheet</a></li>
				<li>You can save your own version with File &gt; make a copy.</li>
				<li>I use <a href="https://gsuite.google.com/u/0/marketplace/app/export_sheet_data/903838927001">this add-on</a> for the json export</li>
				<li>After export, you will need to edit weapons.json to make it work:<ul>
				<li>Take the value of "IDCopy" and put it before that weapon group's opening curly brace, then delete the line for "IDCopy".</li>
				<li>Do this for every weapon (or use keyboard shortcuts in a code editor to do them all at once, eg Ctrl+F2 in VS Code)</li>
				<li>See the screenshots in the Misc folder for reference.</li>
				<li>The proper release won't have this extra editing step.</li>
			</ul></li>
			</ul>

			<h2>Debug Log</h2>

			<p>To use the log options, add these launch options via Steam:</p>
			<pre><code>-debugoutput log-debug.txt -output log-output.txt</code></pre>
			<p>Two log files will appear in the DEADBOLT folder. They'll be appended every time to boot the game, and will keep growing in size until you remove the launch options.</p>
			<ul>
				<li>log-debug: The default GameMaker log. Helps find obvious errors, but not very interesting</li>
				<li>log-output: Custom logs by Hopoo and me. My logs tend to be wrapped in [square brackets]. "FAILED_KEY" logs are normal</li>
			</ul>
			<p>Enable the options <code>debuglog</code> in the JSON config for even more info, but be warned: these are disabled by default because of how much data they output.</p>

			<h2>Custom Icon</h2>

			<p>Unlike DECP, DCP doesn't come bundled with an EXE. If you would like to use a custom icon, one is provided in the Misc folder.</p>
			<p>To use it, pin DEADBOLT to your taskbar, right-click, choose Properties, then Change Icon.</p>
		</section>
	</div>
</template>

<script>
	import LevelConfigSettingsTable from '@/components/content/sections/LevelConfigSettingsTable'

	export default {
		name: 'GuidesContent',
		components: {
			LevelConfigSettingsTable,
		}
	}
</script>
